import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiHeaderOption } from '../../../../shared/constants/api';
import {
  IDeleteProjectValidationResult,
  IProject,
  IProjectCustomLabel,
  IProjectLinkedProperty,
  IProjectWithOwner,
  IProjectKeyExistsResponse,
} from '../../../../shared/interfaces/project.interface';
import {
  IProjectCreateRequest,
  IProjectUpdateRequest,
  IProjectConnectIssueTrackingRequest,
  IUpdateProjectCustomLabelsRequest,
} from '../../../../shared/interfaces/project.requests.interface';
import {
  IGetUserTasks,
  ITask,
  ITaskComment,
  ITaskUserComment,
  ITaskWithLinkedFindingsAndAttachments,
  ITaskWithProject,
  ITasksSummaryByDp,
} from '../../../../shared/interfaces/task.interface';
import {
  IDownloadProjectTasksFilterRequest,
  IGetProjectTasks,
  ITaskCreateFromSuccessCriteriaRequest,
  ITaskCreateRequest,
  ITaskFilterRequest,
  IManualTrackingUpdateRequest,
  ITaskUserCommentCreateRequest,
  ITaskUserCommentEditRequest,
} from '../../../../shared/interfaces/task.requests.interface';
import { ResourceUtility } from '../../utility/resource.utility';
import { RestBuilder } from '../helpers/rest.builder';

@Injectable({
  providedIn: 'root',
})
export class ProjectsRestAPI {
  private restBuilder: RestBuilder;

  constructor(private httpClient: HttpClient) {
    this.restBuilder = new RestBuilder();
  }

  public getLinkedProperty(projectId: string): Observable<IProjectLinkedProperty> {
    const url: string = this.restBuilder.create().projects(projectId).linkedProperty().getApiUrl();
    return this.httpClient.get<IProjectLinkedProperty>(url);
  }

  // POST /workspaces/:workspaceId/projects
  public createProject(workspaceId: string, project: IProjectCreateRequest): Observable<IProject> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects().getApiUrl();
    return this.httpClient.post<IProject>(url, project);
  }

  // POST /workspaces/:workspaceId/projects/:projectId/tasks
  public createTask(workspaceId: string, projectId: string, task: FormData): Observable<ITask> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).tasks().getApiUrl();

    return this.httpClient.post<ITask>(url, task);
  }

  // POST /workspaces/:workspaceId/projects/:projectId/tasks/bulk
  public createTasks(workspaceId: string, projectId: string, tasks: ITaskCreateRequest[]): Observable<ITask[]> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).tasks().bulk().getApiUrl();

    return this.httpClient.post<ITask[]>(url, tasks);
  }

  // POST /workspaces/:workspaceId/projects/:projectId/tasks/successCriterias/bulk
  public createTasksForSuccessCriterias(
    workspaceId: string,
    projectId: string,
    request: ITaskCreateFromSuccessCriteriaRequest,
  ): Observable<ITask[]> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .projects(projectId)
      .tasks()
      .successCriteria()
      .bulk()
      .getApiUrl();

    return this.httpClient.post<ITask[]>(url, request);
  }

  // PUT /workspaces/:workspaceId/projects/:projectId/tasks/:taskId
  public editTask(workspaceId: string, projectId: string, taskId: string, task: FormData): Observable<ITask> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).tasks(taskId).getApiUrl();
    return this.httpClient.put<ITask>(url, task);
  }

  // PATCH /workspaces/:workspaceId/projects/:projectId/tasks/:taskId/manual-tracking
  public updateTaskManualTracking(
    workspaceId: string,
    projectId: string,
    taskId: string,
    request: IManualTrackingUpdateRequest,
  ): Observable<ITask> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .projects(projectId)
      .tasks(taskId)
      .manualTracking()
      .getApiUrl();
    return this.httpClient.patch<ITask>(url, request);
  }

  // POST /workspaces/:workspaceId/projects/:projectId
  public updateProject(
    workspaceId: string,
    projectId: string,
    project: IProjectUpdateRequest | IProjectConnectIssueTrackingRequest, // TODO EAP-32189: revisit service setup in the backend to validate this use case
  ): Observable<IProjectWithOwner> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).getApiUrl();
    return this.httpClient.post<IProjectWithOwner>(url, project);
  }

  // GET /workspaces/:workspaceId/projects/:projectId/tasks
  public getProjectTasks(
    workspaceId: string,
    projectId: string,
    filters: ITaskFilterRequest,
    skipLoader?: boolean,
  ): Observable<IGetProjectTasks> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).tasks().getApiUrl();

    let params: HttpParams = new HttpParams();
    for (const [field, value] of Object.entries(filters)) {
      params = params.set(field, value);
    }

    return this.httpClient.get<IGetProjectTasks>(url, {
      params: params,
      headers: {
        [ApiHeaderOption.skipLoader]: skipLoader === true ? 'true' : 'false',
      },
    });
  }

  // GET /workspaces/:workspaceId/projects/tasks
  public getAllProjectTasks(workspaceId: string, filters: ITaskFilterRequest): Observable<ITaskWithProject[]> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects().tasks().getApiUrl();

    let params: HttpParams = new HttpParams();
    for (const [field, value] of Object.entries(filters)) {
      params = params.set(field, value);
    }

    return this.httpClient.get<ITaskWithProject[]>(url, { params });
  }

  // GET /workspaces/:workspaceId/projects/validate-tasks/digital-properties/summary
  public getTasksSummaryByDp(workspaceId: string, filters: ITaskFilterRequest): Observable<ITasksSummaryByDp[]> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .projects()
      .validateTasks()
      .digitalProperties()
      .summary()
      .getApiUrl();

    let params: HttpParams = new HttpParams();
    for (const [field, value] of Object.entries(filters)) {
      params = params.set(field, value);
    }

    return this.httpClient.get<ITasksSummaryByDp[]>(url, { params });
  }

  // GET /workspaces/:workspaceId/projects
  public getProjects(workspaceId: string): Observable<IProject[]> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects().getApiUrl();
    return this.httpClient.get<IProject[]>(url);
  }

  // GET /workspaces/:workspaceId/projects/:projectId/tasks/:taskId
  public getProjectTask(
    workspaceId: string,
    projectId: string,
    taskId: string,
  ): Observable<ITaskWithLinkedFindingsAndAttachments> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).tasks(taskId).getApiUrl();

    return this.httpClient.get<ITaskWithLinkedFindingsAndAttachments>(url);
  }

  // DELETE /workspaces/:workspaceId/projects/:projectId/tasks/:taskId
  public deleteProjectTask(workspaceId: string, projectId: string, taskId: string): Observable<void> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).tasks(taskId).getApiUrl();

    return this.httpClient.delete<void>(url);
  }

  // DELETE /workspaces/:workspaceId/projects/:projectId/tasks
  public deleteProjectTasks(workspaceId: string, projectId: string, taskIds: string[]): Observable<void> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      body: { taskIds: taskIds },
    };
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).tasks().getApiUrl();

    return this.httpClient.delete<void>(url, httpOptions);
  }

  // GET /workspaces/:workspaceId/projects/:projectId/tasks/:taskId/comments
  public getTaskComments(workspaceId: string, projectId: string, taskId: string): Observable<ITaskComment[]> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .projects(projectId)
      .tasks(taskId)
      .comments()
      .getApiUrl();

    return this.httpClient.get<ITaskUserComment[]>(url);
  }

  // POST /workspaces/:workspaceId/projects/:projectId/tasks/:taskId/comments
  public createTaskComment(
    workspaceId: string,
    projectId: string,
    taskId: string,
    comment: ITaskUserCommentCreateRequest,
  ): Observable<ITaskUserComment> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .projects(projectId)
      .tasks(taskId)
      .comments()
      .getApiUrl();

    return this.httpClient.post<ITaskUserComment>(url, comment);
  }

  public editTaskComment(
    workspaceId: string,
    projectId: string,
    taskId: string,
    commentId: string,
    comment: ITaskUserCommentEditRequest,
  ): Observable<ITaskUserComment> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .projects(projectId)
      .tasks(taskId)
      .comments(commentId)
      .getApiUrl();

    return this.httpClient.patch<ITaskUserComment>(url, comment);
  }

  // GET /workspaces/:workspaceId/projects/:projectId
  public getProject(workspaceId: string, projectId: string): Observable<IProjectWithOwner> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).getApiUrl();
    return this.httpClient.get<IProjectWithOwner>(url);
  }

  // GET /workspaces/:workspaceId/projects/:projectId/custom-labels
  public getCustomLabels(workspaceId: string, projectId: string): Observable<IProjectCustomLabel[]> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).customLabels().getApiUrl();
    return this.httpClient.get<IProjectCustomLabel[]>(url);
  }

  // PUT /workspaces/:workspaceId/projects/:projectId/custom-labels
  public updateCustomLabels(
    workspaceId: string,
    projectId: string,
    request: IUpdateProjectCustomLabelsRequest,
  ): Observable<IProjectCustomLabel[]> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).customLabels().getApiUrl();
    return this.httpClient.put<IProjectCustomLabel[]>(url, request);
  }

  public deleteCustomLabel(workspaceId: string, projectId: string, customLabelId: string): Observable<void> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .projects(projectId)
      .customLabels()
      .id(customLabelId)
      .getApiUrl();
    return this.httpClient.delete<void>(url);
  }

  // GET /workspaces/:workspaceId/projects/keys/:keyId
  public projectKeyExists(workspaceId: string, projectKey: string): Observable<IProjectKeyExistsResponse> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects().key(projectKey).getApiUrl();
    return this.httpClient.get<IProjectKeyExistsResponse>(url);
  }

  // DELETE /workspaces/:workspaceId/projects/:projectId
  public deleteProject(workspaceId: string, projectId: string): Observable<void> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).getApiUrl();

    return this.httpClient.delete<void>(url);
  }

  // PUT /workspaces/:workspaceId/projects/:projectId/archive
  public archiveProject(workspaceId: string, projectId: string): Observable<void> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).archive().getApiUrl();

    return this.httpClient.put<void>(url, {});
  }

  // PUT /workspaces/:workspaceId/projects/:projectId/restore
  public restoreProject(workspaceId: string, projectId: string): Observable<void> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).restore().getApiUrl();

    return this.httpClient.put<void>(url, {});
  }

  // GET /workspaces/:workspaceId/project/:projectId/archivable
  public isProjectArchivable(workspaceId: string, projectId: string): Observable<boolean> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).archivable().getApiUrl();

    return this.httpClient.get<boolean>(url);
  }

  // GET /workspaces/:workspaceId/project/:projectId/deletable
  public validateProjectDeletion(workspaceId: string, projectId: string): Observable<IDeleteProjectValidationResult> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).deletable().getApiUrl();

    return this.httpClient.get<IDeleteProjectValidationResult>(url);
  }

  // POST /workspaces/:workspaceId/projects/:projectId/tasks/download
  public downloadProjectTasks(
    workspaceId: string,
    projectId: string,
    filters: IDownloadProjectTasksFilterRequest = {},
  ): Observable<void> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).tasks().download().getApiUrl();
    return this.httpClient
      .post(url, filters, { observe: 'response', responseType: 'arraybuffer' })
      .pipe(map(ResourceUtility.downloadResponse));
  }

  // GET /workspaces/:workspaceId/my-tasks
  public getMyTasks(workspaceId: string, filters: ITaskFilterRequest): Observable<IGetUserTasks> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).myTasks().getApiUrl();

    let params: HttpParams = new HttpParams();
    for (const [field, value] of Object.entries(filters)) {
      params = params.set(field, value);
    }

    return this.httpClient.get<IGetUserTasks>(url, { params: params });
  }

  // GET /workspaces/:workspaceId/projects/:projectId/issue-integration-statuses
  public getUsedIssueTrackingStatuses(workspaceId: string, projectId: string): Observable<string[]> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .projects(projectId)
      .issueTrackingIssueStatuses()
      .getApiUrl();

    return this.httpClient.get<string[]>(url);
  }

  // POST /workspaces/:workspaceId/projects/validate-tasks
  public validateTasks(workspaceId: string, formData: FormData): Observable<void> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects().validateTasks().getApiUrl();
    return this.httpClient.post<void>(url, formData);
  }

  public getTaskProjectId(workspaceId: string, projectId: string): Observable<{ taskProjectId: string }> {
    const url: string = this.restBuilder.create().workspaces(workspaceId).projects(projectId).taskProjectId().getApiUrl();
    return this.httpClient.post<{ taskProjectId: string }>(url, {});
  }
}
